"use client";

import { Button, Carousel, CarouselApi, CarouselContent, CarouselItem, cn } from "@saridana-core/react-ui-kit";
import { TLocationSection } from "@shared/types";
import { SectionHeading } from "@shared/ui";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

export function LocationSection({ title, address, mapButtonLink, embedMapLink, images }: TLocationSection) {
  const [api, setApi] = useState<CarouselApi>();

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  useEffect(() => {
    if (!api) return;

    setCurrentIndex(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrentIndex(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <section
      className="py-[80px] sm:py-[100px] lg:py-[120px] relative"
      style={{
        background: "#F6FAFD",
      }}
    >
      <div className="container relative">
        <div className="flex flex-col lg:flex-row gap-14 md:gap-[72px] lg:items-end">
          {title && (
            <div className="w-full lg:w-[45%] flex flex-col justify-between">
              <div>
                <SectionHeading
                  title={title}
                  titleClassName="mx-0 text-5xl sm:text-6xl leading-[115%]"
                  description={address}
                  descriptionClassName="text-xl sm:text-2xl leading-[140%]"
                />
                {mapButtonLink && (
                  <Button
                    asChild
                    variant="link"
                    className="p-0 m-0 h-auto text-primary-600 font-semibold mt-6 sm:mt-10 text-xl md:text-2xl"
                  >
                    <Link href={mapButtonLink.href} target={mapButtonLink.target || "_blank"}>
                      {mapButtonLink.text}
                      {mapButtonLink.icon && mapButtonLink.icon?.data && (
                        <Image
                          className="size-6 mr-1"
                          src={mapButtonLink.icon?.data?.attributes?.url || ""}
                          alt={
                            mapButtonLink.icon?.data?.attributes?.alternativeText ||
                            mapButtonLink.icon?.data?.attributes?.name ||
                            "Location map button icon"
                          }
                          width={mapButtonLink.icon?.data?.attributes?.width || 28}
                          height={mapButtonLink.icon?.data?.attributes?.height || 28}
                        />
                      )}
                    </Link>
                  </Button>
                )}
              </div>
              {mapButtonLink && (
                <div className="flex-1 space-y-12 md:space-y-[60px] mt-12 md:mt-[60px] w-full lg:w-[80%]">
                  <div className="overflow-hidden rounded-2xl h-[280px] sm:h-[320px] w-full">
                    <iframe
                      className="w-full"
                      src={embedMapLink}
                      width="100%"
                      height="100%"
                      loading="lazy"
                      title="location map"
                      style={{ border: 0 }}
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {images && images.data && images.data.length > 0 && (
            <div className="flex-1 w-full relative">
              <Carousel className="z-1 h-container" opts={{ align: "start", loop: true }} setApi={setApi}>
                <CarouselContent className="h-full w-container min-h-[480px] md:min-h-[600px] lg:min-h-[700px]">
                  {images.data.map((image, index) => {
                    return (
                      <CarouselItem key={"location-section-image-" + image.attributes.name + "-" + index}>
                        <Image
                          src={image.attributes?.url || ""}
                          alt={image.attributes?.alternativeText || image.attributes?.name || ""}
                          width={image.attributes?.width || 648}
                          height={image.attributes?.height || 732}
                          className="w-full h-full object-cover object-center bg-gray-100 rounded-2xl"
                        />
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
              </Carousel>
              {images && images.data && images.data.length > 1 && (
                <div className="absolute bottom-5 z-2 w-full flex justify-center text-center items-center gap-2.5">
                  {images.data.map((_, index) => {
                    return (
                      <a
                        key={"location-section-slide-indicator-" + index}
                        className={cn(
                          "inline-block rounded-full size-4 cursor-pointer border border-white",
                          index === currentIndex ? "bg-primary-600" : "bg-white/50",
                        )}
                        onClick={() => api?.scrollTo(index)}
                      >
                        &nbsp;
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
